import { useState, useEffect, useCallback } from 'react';
import { Sun, Settings, DollarSign, MapPin, Zap, AlertCircle, ArrowLeft, Compass, Download, FileText } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, BarChart, Bar
} from 'recharts';
import { useSubscriptionStore } from '../store/subscriptionStore';
import { SubscriptionSelector } from './SubscriptionSelector';
import { Footer } from './Footer';
import { BOQDisplay } from './BOQDisplay';
import type { LocationData, SolarDesign, FinancialData, BillData, UserType } from '../types';
import { useNavigate } from 'react-router-dom';
import { calculateSolarSystem, calculateSystemSizeFromConsumption, type CalculationInputs, type CalculationResults } from '../services/solarCalculationEngine';
import { useAuthStore } from '../store/authStore';
import { uploadPdfAndRecord } from '../services/projectDocuments';
import { findNearestCity } from '../data/saudiWeatherData';
import { generatePDFReport } from '../services/reportGenerator';
import { generateCalculationSourcesPDF, type CalculationSourcesData } from '../services/calculationSourcesReport';
import { calculateBOQ, type SystemConfiguration, type BOQSummary } from '../services/boqCalculationService';
import { calculateFinancials, type FinancialInputs } from '../services/financialCalculationService';
import { sizeCable, type CableInstallationMethod, type CablePhase } from '../services/cableSizingService';
import { fetchCablePricingTable } from '../services/cablePricingService';
import { fetchRoofFixedMountingPricing } from '../services/mountingPricingService';
import { getEffectiveConsumption } from '../utils/consumptionNormalization';
import { resolveInverterConstrainedSizing } from '../utils/inverterSizing';
import {
  fetchPricingFactors,
  selectMultiInverterPackAtOrBelow,
  selectModule,
  selectSingleInverterAtOrBelow,
  type PricingFactors,
  type SelectedInverter,
  type SelectedModule
} from '../services/equipmentSelectionService';

export interface SolarDesignPersistExtras {
  calculationResults: CalculationResults | null;
  calculationInputs: CalculationInputs | null;
  pricingFactors: PricingFactors | null;
  moduleSnapshot: Record<string, unknown> | null;
  inverterSnapshot: Record<string, unknown> | null;
}

function toCablePhase(value: unknown): CablePhase | undefined {
  const n = typeof value === 'number' ? value : Number(value);
  return n === 1 || n === 3 ? (n as CablePhase) : undefined;
}

function installationMethodFromMountingType(mountingType: unknown): CableInstallationMethod {
  const mt = String(mountingType || '');
  // Per requirement: ground-mounted and carport-mounted installations -> direct in ground.
  if (
    mt === 'ground-fixed' ||
    mt === 'single-axis' ||
    mt === 'single-axis-backtracking' ||
    mt === 'dual-axis' ||
    mt === 'carport-mounted' ||
    mt === 'carport'
  ) {
    return 'direct_in_ground';
  }
  return 'enclosed_in_conduit_on_wall';
}

function deriveDcDesignFromStringing(args: {
  panelCount: number;
  inverterCount: number;
  module: SelectedModule | null;
  inverter: SelectedInverter | null;
}): { designCurrentA: number; designVoltageV: number; modulesPerString: number; stringsPerInverter: number } | null {
  const { panelCount, inverterCount, module, inverter } = args;
  console.log('\n  🔋 PV Stringing Calculation:');
  console.log('    Input Parameters:');
  console.log('      - Panel Count:', panelCount);
  console.log('      - Inverter Count:', inverterCount);
  console.log('      - Module:', module ? `${module.manufacturer} ${module.model}` : 'null');
  console.log('      - Inverter:', inverter ? `${inverter.manufacturer} ${inverter.model}` : 'null');
  
  if (!module || !inverter) {
    console.log('    ⚠️ Cannot calculate - missing module or inverter');
    return null;
  }
  if (panelCount <= 0 || inverterCount <= 0) {
    console.log('    ⚠️ Cannot calculate - invalid panel or inverter count');
    return null;
  }
  if (module.vmp <= 0 || module.voc <= 0 || module.isc <= 0) {
    console.log('    ⚠️ Cannot calculate - invalid module electrical parameters');
    return null;
  }

  console.log('    Module Electrical Parameters:');
  console.log('      - Vmp (V):', module.vmp);
  console.log('      - Voc (V):', module.voc);
  console.log('      - Isc (A):', module.isc);
  console.log('    Inverter DC Parameters:');
  console.log('      - DC MPP Min Voltage (V):', inverter.voltage_dc_mpp_min);
  console.log('      - DC MPP Max Voltage (V):', inverter.voltage_dc_mpp_max);
  console.log('      - DC Max Voltage (V):', inverter.voltage_dc_max);

  // Choose a plausible modules-per-string count within the inverter MPP window.
  const minMps = Math.max(1, Math.ceil(inverter.voltage_dc_mpp_min / module.vmp));
  let mps = Math.max(1, Math.floor(inverter.voltage_dc_mpp_max / module.vmp));
  if (mps < minMps) mps = minMps;

  console.log('    String Configuration:');
  console.log('      - Min Modules Per String:', minMps, `(ceil(${inverter.voltage_dc_mpp_min} / ${module.vmp}))`);
  console.log('      - Initial Modules Per String:', mps, `(floor(${inverter.voltage_dc_mpp_max} / ${module.vmp}))`);

  // Basic Voc sanity against inverter max DC voltage (STC-based; cold Voc is higher).
  while (mps > 1 && mps * module.voc > inverter.voltage_dc_max) {
    mps -= 1;
  }

  console.log('      - Final Modules Per String (after Voc check):', mps);
  if (mps * module.voc > inverter.voltage_dc_max) {
    console.log('        ⚠️ Voc exceeds inverter max:', mps * module.voc, '>', inverter.voltage_dc_max);
  }

  const totalStrings = Math.ceil(panelCount / mps);
  const stringsPerInverter = Math.ceil(totalStrings / inverterCount);

  console.log('      - Total Strings:', totalStrings, `(ceil(${panelCount} / ${mps}))`);
  console.log('      - Strings Per Inverter:', stringsPerInverter, `(ceil(${totalStrings} / ${inverterCount}))`);

  // IEC PV practice often uses ~1.25× Isc for design current per string.
  const designCurrentA = 1.25 * module.isc * stringsPerInverter;
  const designVoltageV = mps * module.vmp;

  console.log('    Design Values:');
  console.log('      - Design Current (A):', designCurrentA.toFixed(2), `(1.25 × ${module.isc} × ${stringsPerInverter})`);
  console.log('      - Design Voltage (V):', designVoltageV, `(${mps} × ${module.vmp})`);

  return { designCurrentA, designVoltageV, modulesPerString: mps, stringsPerInverter };
}

interface SolarDesignViewProps {
  location: LocationData;
  billData?: BillData;
  userType?: UserType;
  projectId?: string | null;
  pvDesignId?: string | null;
  onNext: () => void;
  onBack: () => void;
  onDesignCalculated: (design: SolarDesign, financials: FinancialData, boq: BOQSummary, extras: SolarDesignPersistExtras) => void;
}

export function SolarDesignView({
  location,
  billData,
  userType = 'residential',
  projectId = null,
  pvDesignId = null,
  onNext,
  onBack,
  onDesignCalculated,
}: SolarDesignViewProps) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [isLoading, setIsLoading] = useState(true);
  const [design, setDesign] = useState<SolarDesign | null>(null);
  const [financials, setFinancials] = useState<FinancialData | null>(null);
  const [boq, setBOQ] = useState<BOQSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showBOQ, setShowBOQ] = useState(false);
  const [pricingFactors, setPricingFactors] = useState<PricingFactors | null>(null);
  const [selectedModule, setSelectedModule] = useState<SelectedModule | null>(null);
  const [selectedInverter, setSelectedInverter] = useState<SelectedInverter | null>(null);
  const [calculationResults, setCalculationResults] = useState<CalculationResults | null>(null);
  const effectiveYearlyConsumptionForDisplay = getEffectiveConsumption(billData).effectiveYearlyConsumption;
  const displayedOffsetPct =
    design && effectiveYearlyConsumptionForDisplay > 0
      ? Math.min(100, Math.round((design.yearlyProduction / effectiveYearlyConsumptionForDisplay) * 100))
      : (design?.offset ?? 0);

  const { checkSystemSizeLimit, getCurrentPlan } = useSubscriptionStore();

  const calculateDesign = useCallback(async (loc: LocationData) => {
    try {
      // Handle new multi-location structure vs legacy single-location
      let latitude, longitude, altitude, tilt, azimuth, mountingType, surfaceType;
      
      if (loc.locations && loc.locations.length > 0) {
        // New multi-location structure - use first location for calculations
        const primaryLocation = loc.locations[0];
        latitude = primaryLocation.latitude;
        longitude = primaryLocation.longitude;
        altitude = 0; // Default altitude
        tilt = primaryLocation.tilt;
        azimuth = primaryLocation.azimuth;
        mountingType = primaryLocation.mounting_type;
        surfaceType = primaryLocation.surface_type;
      } else {
        // Legacy single-location structure
        latitude = loc.latitude || 24.7136;
        longitude = loc.longitude || 46.6753;
        altitude = 0;
        tilt = loc.tilt || 10;
        azimuth = loc.azimuth || 180;
        mountingType = loc.mountingType || 'roof-fixed';
        surfaceType = loc.surfaceType || 'asphalt';
      }

      // Weather data will be fetched automatically by the calculation engine
      // (NREL-only: NSRDB TMY download provides irradiance + temperature + wind)
      
      const effectiveConsumption = getEffectiveConsumption(billData);
      const yearlyConsumption = effectiveConsumption.effectiveYearlyConsumption;
      const monthlyConsumption = effectiveConsumption.effectiveMonthlyConsumption;

      // Calculate initial system size based on consumption (async with weather fetching)
      const initialSystemSize = await calculateSystemSizeFromConsumption(
        monthlyConsumption,
        { latitude, longitude }, // Will fetch weather data automatically
        1.0 // 100% offset target
      );
      const MAX_DC_AC_RATIO = 1.3;

      // Pricing + equipment selection (Supabase component database)
      const factors = await fetchPricingFactors();
      setPricingFactors(factors);
      const cablePricing = await fetchCablePricingTable();
      const roofFixedMountingPricing = await fetchRoofFixedMountingPricing();

      const preferenceFromLocation =
        loc.locations?.[0]?.equipment_preference ||
        loc.equipmentPreference ||
        'median';

      const module = await selectModule(preferenceFromLocation);
      setSelectedModule(module);
      const modulePower = module?.power_stc || 550;

      // Inverter-constrained sizing flow
      const constrainedSizingFlow = await resolveInverterConstrainedSizing({
        initialSystemSizeKw: initialSystemSize,
        modulePowerW: modulePower,
        userType: userType as UserType,
        preference: preferenceFromLocation,
        maxDcAcRatio: MAX_DC_AC_RATIO,
        selectSingle: selectSingleInverterAtOrBelow,
        selectMulti: selectMultiInverterPackAtOrBelow,
      });
      const acCapKw = constrainedSizingFlow.acCapKw;
      const inverterSelection = constrainedSizingFlow.selection;
      const inverter = inverterSelection.selectedInverter;
      if (!inverter || inverterSelection.inverterCount < 1 || inverterSelection.selectedTotalAcKw <= 0) {
        setError('No inverter options are available from the equipment database.');
        return null;
      }
      setSelectedInverter(inverter);

      const inverterUnitKw = inverter.power_ac_nominal;
      const inverterCount = inverterSelection.inverterCount;
      const totalInverterKw = inverterSelection.selectedTotalAcKw;
      const constrainedPanelCount = constrainedSizingFlow.panelCount;
      const constrainedSystemSizeKw = constrainedSizingFlow.finalSystemSizeKw;
      const actualDcAcRatio = constrainedSizingFlow.finalDcAcRatio;
      if (actualDcAcRatio > MAX_DC_AC_RATIO + 1e-9) {
        setError('Unable to enforce the DC/AC ratio limit (1.3) with available inverter/module sizes.');
        return null;
      }

      if (!checkSystemSizeLimit(constrainedSystemSizeKw)) {
        const currentPlan = getCurrentPlan();
        setError(`System size (${constrainedSystemSizeKw.toFixed(1)}kW) exceeds your plan's limit of ${currentPlan?.systemSizeLimit}kW`);
        setShowUpgrade(true);
        return null;
      }

      // Prepare calculation inputs (cityWeatherData is optional - will be fetched)
      const calculationInputs: CalculationInputs = {
        latitude,
        longitude,
        altitude,
        systemSize: constrainedSystemSizeKw,
        panelCountOverride: constrainedPanelCount,
        inverterSizeOverrideKw: totalInverterKw,
        tilt,
        azimuth,
        mountingType,
        surfaceType,
        monthlyConsumption,
        yearlyConsumption,
        shadingLevel: 'none', // Default, can be made configurable
        modulePower,
        userType: userType as any
      };

      // Run the calculation engine (async with NREL-only weather service)
      const results = await calculateSolarSystem(calculationInputs);

      // Calculate energy offset
      const offset = yearlyConsumption > 0
        ? Math.min(100, Math.round((results.annualProduction / yearlyConsumption) * 100))
        : 0;

      // Inverter AC is selected by constrained sizing flow above.
      const requiredAcKw = totalInverterKw;
      console.log('=== CABLE SIZING CALCULATIONS ===');
      console.log('📊 System Parameters:');
      console.log('  - Initial System Size (kW):', initialSystemSize);
      console.log('  - Inverter AC Cap (kW):', acCapKw);
      console.log('  - Selection Mode:', inverterSelection.selectionMode);
      console.log('  - System Size (kW):', results.systemSize);
      console.log('  - DC/AC Ratio (max):', MAX_DC_AC_RATIO);
      console.log('  - Required AC Power (kW):', requiredAcKw);
      console.log('  - Panel Count:', results.panelCount);

      console.log('🔌 Inverter Selection:');
      console.log('  - Selected Inverter:', inverter.manufacturer, inverter.model);
      console.log('  - Inverter Unit Power (kW):', inverterUnitKw);
      console.log('  - Inverter Count:', inverterCount);
      console.log('  - Total Inverter AC Power (kW):', totalInverterKw);
      console.log('  - DC/AC Ratio (actual):', actualDcAcRatio, actualDcAcRatio <= MAX_DC_AC_RATIO ? '(OK)' : '(> 1.3 ⚠️)');
      console.log('  - Inverter Phases:', inverter.phases);
      console.log('  - Inverter Max DC Current (A):', inverter.current_dc_max);
      console.log('  - Inverter DC MPP Min Voltage (V):', inverter.voltage_dc_mpp_min);

      // Derive cable sizing loads (AC from kW, DC from inverter limits; PV-stringing fallback)
      const gridPhase: CablePhase =
        toCablePhase(loc.electrical?.gridPhase) ??
        toCablePhase(inverter?.phases) ??
        (userType === 'residential' ? 1 : 3);

      const installationMethod = installationMethodFromMountingType(mountingType);
      const acLengthM = loc.electrical?.acCableDistanceM;
      const dcLengthM = loc.electrical?.dcCableDistanceM;

      console.log('⚡ Electrical Configuration:');
      console.log('  - Grid Phase (from location):', loc.electrical?.gridPhase);
      console.log('  - Grid Phase (final):', gridPhase);
      console.log('  - Installation Method:', installationMethod);
      console.log('  - AC Cable Distance (m):', acLengthM);
      console.log('  - DC Cable Distance (m):', dcLengthM);

      const derivedDc = deriveDcDesignFromStringing({
        panelCount: results.panelCount,
        inverterCount,
        module,
        inverter,
      });

      console.log('🔋 DC Design from PV Stringing:');
      if (derivedDc) {
        console.log('  - Modules Per String:', derivedDc.modulesPerString);
        console.log('  - Strings Per Inverter:', derivedDc.stringsPerInverter);
        console.log('  - Design Current (A):', derivedDc.designCurrentA);
        console.log('  - Design Voltage (V):', derivedDc.designVoltageV);
        if (module) {
          console.log('  - Module Isc (A):', module.isc);
          console.log('  - Module Vmp (V):', module.vmp);
          console.log('  - Module Voc (V):', module.voc);
        }
      } else {
        console.log('  - Could not derive DC design (missing module/inverter data)');
      }

      const dcDesignCurrentA =
        inverter?.current_dc_max && inverter.current_dc_max > 0
          ? inverter.current_dc_max
          : derivedDc?.designCurrentA;

      // CRITICAL FIX: Use actual PV string operating voltage, NOT inverter MPPT minimum
      // The inverter MPPT minimum (e.g., 100V) is just the range limit, not the operating voltage
      // The actual string voltage (e.g., 441V = 14 panels × 31.5V) is what we need for voltage drop calculation
      const dcDesignVoltageV = derivedDc?.designVoltageV ?? 600;

      console.log('📏 Final DC Design Values:');
      console.log('  - DC Design Current (A):', dcDesignCurrentA, 
        dcDesignCurrentA === inverter?.current_dc_max ? '(from inverter spec)' : '(from PV stringing)');
      console.log('  - DC Design Voltage (V):', dcDesignVoltageV,
        derivedDc?.designVoltageV ? '(from PV stringing - CORRECT)' : '(fallback default)');
      if (inverter?.voltage_dc_mpp_min && inverter.voltage_dc_mpp_min > 0) {
        console.log('  - ⚠️ Inverter MPPT Min (V):', inverter.voltage_dc_mpp_min, '(NOT used - this is just the MPPT range minimum, not operating voltage)');
      }

      // Calculate BOQ (Bill of Quantities) with accurate Saudi market pricing
      const boqConfig: SystemConfiguration = {
        systemSize: results.systemSize,
        panelCount: results.panelCount,
        modulePower,
        inverterSize: inverterUnitKw,
        dcAcRatio: MAX_DC_AC_RATIO,
        mountingType: mountingType as any,
        userType: userType as any,
        location: {
          latitude,
          longitude
        },
        selectedModule: module ? {
          manufacturer: module.manufacturer,
          model: module.model,
          power_stc: module.power_stc,
          efficiency: module.efficiency,
          warranty_years: module.warranty_years,
          cell_type: module.cell_type,
          bifacial: module.bifacial,
          bifaciality_factor: module.bifaciality_factor,
          price_usd: module.price_usd
        } : undefined,
        selectedInverter: inverter ? {
          manufacturer: inverter.manufacturer,
          model: inverter.model,
          power_ac_nominal: inverter.power_ac_nominal,
          phases: inverter.phases,
          inverter_type: inverter.inverter_type,
          efficiency_max: inverter.efficiency_max,
          efficiency_euro: inverter.efficiency_euro,
          warranty_years: inverter.warranty_years,
          price_usd: inverter.price_usd
        } : undefined,
        pricing: {
          usdToSar: factors.usdToSar,
          vatRate: factors.vatRate
        },
        cablePricing: cablePricing ?? undefined,
        mountingPricing: roofFixedMountingPricing
          ? {
              roofFixed: roofFixedMountingPricing,
            }
          : undefined,
        cables: {
          ac:
            typeof acLengthM === 'number' && acLengthM > 0
              ? (() => {
                  console.log('\n🔌 AC CABLE SIZING:');
                  console.log('  Input Parameters:');
                  console.log('    - System:', 'AC');
                  console.log('    - Phase:', gridPhase);
                  console.log('    - Length (m):', acLengthM);
                  console.log('    - Load (kW):', requiredAcKw);
                  console.log('    - Installation Method:', installationMethod);
                  
                  const sizing = sizeCable({
                    system: 'AC',
                    phase: gridPhase,
                    lengthM: acLengthM,
                    load: { unit: 'kW', value: requiredAcKw },
                    installationMethod,
                  });
                  
                  console.log('  Results:');
                  console.log('    - Selected Size (mm²):', sizing.sizeMm2);
                  console.log('    - Current (A):', sizing.currentA);
                  console.log('    - Voltage (V):', sizing.voltageV);
                  console.log('    - Loaded Cores:', sizing.loadedCores);
                  console.log('    - Ampacity (A):', sizing.ampacityA);
                  console.log('    - Voltage Drop (V):', sizing.voltageDropV);
                  console.log('    - Voltage Drop (%):', sizing.voltageDropPercent);
                  console.log('    - Max Voltage Drop (%):', sizing.maxVoltageDropPercent);
                  console.log('    - Limiting Factor:', sizing.limiting);
                  console.log('    - Assumptions:', sizing.assumptions);
                  
                  return {
                    lengthM: acLengthM,
                    phase: gridPhase,
                    sizeMm2: sizing.sizeMm2,
                    currentA: sizing.currentA,
                  };
                })()
              : (() => {
                  console.log('\n🔌 AC CABLE SIZING:');
                  console.log('  ⚠️ Skipped - AC cable distance not provided or invalid');
                  return undefined;
                })(),
          dc:
            typeof dcLengthM === 'number' && dcLengthM > 0 && dcDesignCurrentA != null && dcDesignCurrentA > 0
              ? (() => {
                  console.log('\n🔋 DC CABLE SIZING:');
                  console.log('  Input Parameters:');
                  console.log('    - System:', 'DC');
                  console.log('    - Length (m):', dcLengthM);
                  console.log('    - Load (A):', dcDesignCurrentA);
                  console.log('    - Voltage (V):', dcDesignVoltageV);
                  console.log('    - Installation Method:', installationMethod);
                  
                  const sizing = sizeCable({
                    system: 'DC',
                    lengthM: dcLengthM,
                    load: { unit: 'A', value: dcDesignCurrentA },
                    voltageV: dcDesignVoltageV,
                    installationMethod,
                  });
                  
                  console.log('  Results:');
                  console.log('    - Selected Size (mm²):', sizing.sizeMm2);
                  console.log('    - Current (A):', sizing.currentA);
                  console.log('    - Voltage (V):', sizing.voltageV);
                  console.log('    - Loaded Cores:', sizing.loadedCores);
                  console.log('    - Ampacity (A):', sizing.ampacityA);
                  console.log('    - Voltage Drop (V):', sizing.voltageDropV);
                  console.log('    - Voltage Drop (%):', sizing.voltageDropPercent);
                  console.log('    - Max Voltage Drop (%):', sizing.maxVoltageDropPercent);
                  console.log('    - Limiting Factor:', sizing.limiting);
                  console.log('    - Assumptions:', sizing.assumptions);
                  
                  return {
                    lengthM: dcLengthM,
                    sizeMm2: sizing.sizeMm2,
                    currentA: sizing.currentA,
                    voltageV: sizing.voltageV,
                  };
                })()
              : (() => {
                  console.log('\n🔋 DC CABLE SIZING:');
                  console.log('  ⚠️ Skipped - DC cable distance or current not provided or invalid');
                  console.log('    - DC Length (m):', dcLengthM);
                  console.log('    - DC Design Current (A):', dcDesignCurrentA);
                  return undefined;
                })(),
        },
      };
      
      const boqResults = calculateBOQ(boqConfig);
      
      // Calculate detailed financials with accurate Saudi tariffs
      const financialInputs: FinancialInputs = {
        systemSize: results.systemSize,
        annualProduction: results.annualProduction,
        yearlyConsumption,
        customTariffSarPerKwh:
          typeof billData?.customTariffSarPerKwh === 'number' &&
          Number.isFinite(billData.customTariffSarPerKwh) &&
          billData.customTariffSarPerKwh > 0
            ? billData.customTariffSarPerKwh
            : undefined,
        monthlyConsumption,
        monthlyProduction: results.monthlyResults && results.monthlyResults.length === 12
          ? results.monthlyResults.map(m => m.gridOutput)
          : Array(12).fill(results.annualProduction / 12),
        totalCost: boqResults.grandTotal,
        userType: userType as any,
        financingType: 'cash' // Default to cash, can be changed by user
      };
      
      const detailedFinancialResults = calculateFinancials(financialInputs);
      
      // Store BOQ for display
      setBOQ(boqResults);

      const design: SolarDesign = {
        systemSize: results.systemSize,
        panelCount: results.panelCount,
        yearlyProduction: results.annualProduction,
        offset,
        initialSystemSize,
        inverterAcCapKw: acCapKw,
        selectedInverterAcKw: inverterUnitKw,
        selectedInverterCount: inverterCount,
        dcAcRatioFinal: actualDcAcRatio,
        monthlyResults: results.monthlyResults // Store monthly results for display
      };

      // Map detailed financials to legacy FinancialData format for compatibility
      const financials: FinancialData = {
        totalCost: detailedFinancialResults.totalCost,
        federalTaxCredit: 0,
        stateTaxCredit: 0,
        monthlyPayment: detailedFinancialResults.loanDetails?.monthlyPayment || 0,
        paybackPeriod: detailedFinancialResults.paybackPeriod,
        roi: detailedFinancialResults.roi,
        savingsYear1: detailedFinancialResults.firstYearSavings,
        savings25Year: detailedFinancialResults.savings25Year
      };

      const moduleSnapshot = module
        ? (JSON.parse(JSON.stringify(module)) as Record<string, unknown>)
        : null;
      const inverterSnapshot = inverter
        ? (JSON.parse(JSON.stringify(inverter)) as Record<string, unknown>)
        : null;

      return {
        design,
        financials,
        boq: boqResults,
        calculationResults: results,
        calculationInputs,
        pricingFactors: factors,
        moduleSnapshot,
        inverterSnapshot,
      };
    } catch (error) {
      console.error('Calculation error:', error);
      // Provide more specific error messages
      let errorMessage = 'Failed to calculate solar design. Please check your input data.';
      if (error instanceof Error) {
        if (error.message.includes('Invalid calculation inputs')) {
          errorMessage = `Input validation error: ${error.message.replace('Invalid calculation inputs: ', '')}`;
        } else if (error.message.includes('weather') || error.message.includes('NREL') || error.message.includes('NSRDB')) {
          errorMessage =
            'Unable to fetch NREL NSRDB weather data. Check your internet connection and ensure VITE_NREL_API_KEY and VITE_NREL_EMAIL are configured.';
        } else if (error.message.includes('Supabase') || error.message.includes('database')) {
          errorMessage = 'Database connection error. Please try again later.';
        } else {
          errorMessage = `Calculation failed: ${error.message}`;
        }
      }
      setError(errorMessage);
      return null;
    }
  }, [checkSystemSizeLimit, getCurrentPlan, billData, userType]);

  const generateMonthlyData = useCallback(
    (design: SolarDesign) => {
      const monthlyProduction =
        design.monthlyResults && design.monthlyResults.length === 12
          ? design.monthlyResults.map((m) => (Number.isFinite(m.gridOutput) ? m.gridOutput : 0))
          : Array(12).fill((Number.isFinite(design.yearlyProduction) ? design.yearlyProduction : 0) / 12);

      const monthlyConsumption = getEffectiveConsumption(billData).effectiveMonthlyConsumption;

      // Derive tariff from the selected formula: firstYearSavings = tariff * annualProduction.
      const savingsYear1 = financials?.savingsYear1 ?? 0;
      const yearlyProduction = monthlyProduction.reduce(
        (sum, value) => sum + (Number.isFinite(value) ? Math.max(0, value) : 0),
        0
      );
      const tariffSarPerKwh = yearlyProduction > 0 ? savingsYear1 / yearlyProduction : 0;

      return Array.from({ length: 12 }, (_, i) => {
        const month = new Date(2024, i).toLocaleString('default', { month: 'short' });
        const consumption = Number.isFinite(monthlyConsumption[i]) ? monthlyConsumption[i] : 0;
        const generation = Number.isFinite(monthlyProduction[i]) ? monthlyProduction[i] : 0;
        return {
          month,
          consumption,
          generation,
          savings: generation * tariffSarPerKwh,
          newBill: Math.max(0, (consumption - generation) * tariffSarPerKwh),
        };
      });
    },
    [billData, financials]
  );

  useEffect(() => {
    if (!location) {
      setError('Location data is required');
      setIsLoading(false);
      return;
    }

    const calculateSolarDesign = async () => {
      try {
        const result = await calculateDesign(location);
        if (result) {
          const { design, financials, boq, calculationResults: calcResults, calculationInputs, pricingFactors, moduleSnapshot, inverterSnapshot } = result;
          setDesign(design);
          setFinancials(financials);
          setCalculationResults(calcResults ?? null);
          setTimeout(() => {
            onDesignCalculated(design, financials, boq, {
              calculationResults: calcResults ?? null,
              calculationInputs: calculationInputs ?? null,
              pricingFactors: pricingFactors ?? null,
              moduleSnapshot: moduleSnapshot ?? null,
              inverterSnapshot: inverterSnapshot ?? null,
            });
          }, 0);
        }
      } catch (err) {
        console.error('Design calculation error:', err);
        // Provide more specific error messages
        let errorMessage = 'Failed to calculate design. Please try again.';
        if (err instanceof Error) {
          if (err.message.includes('Invalid calculation inputs')) {
            errorMessage = `Input validation failed: ${err.message}`;
          } else if (err.message.includes('weather') || err.message.includes('NREL') || err.message.includes('NSRDB')) {
            errorMessage =
              'Unable to fetch NREL NSRDB weather data. Check your internet connection and ensure VITE_NREL_API_KEY and VITE_NREL_EMAIL are configured.';
          } else if (err.message.includes('API')) {
            errorMessage = 'External service error. Please try again later.';
          } else {
            errorMessage = `Calculation error: ${err.message}`;
          }
        }
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    setIsLoading(true);
    setError(null);
    calculateSolarDesign();
  }, [location, calculateDesign, onDesignCalculated]);

  const handleUpgradeClick = () => {
    navigate('/subscription');
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center border border-gray-200">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Calculating optimal energy design...</p>
        </div>
      </div>
    );
  }

  if (!design || !financials) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
          <div className="text-center text-red-600 mb-4">
            {error || 'Failed to calculate energy design'}
          </div>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <button
              onClick={onBack}
              className="bg-gray-100 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Back
            </button>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {showUpgrade ? (
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 border border-gray-200">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Upgrade Required
            </h2>
            <p className="text-gray-600 mb-6">
              Your current plan doesn't support a system of this size. Please upgrade to continue.
            </p>
            <div className="bg-yellow-50 rounded-lg p-6 mb-8">
              <div className="flex items-start">
                <AlertCircle className="h-6 w-6 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <p className="text-yellow-800 font-medium">System Size Limit Exceeded</p>
                  <p className="mt-2 text-yellow-700">
                    Your calculated system size ({design.systemSize.toFixed(1)}kW) exceeds your current plan's limit.
                    Upgrade your subscription to access larger system designs.
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={handleUpgradeClick}
              className="bg-gradient-to-r from-blue-600 to-green-600 text-white py-3 px-8 rounded-lg hover:from-blue-700 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform transition-all hover:scale-[1.02] active:scale-[0.98] font-medium text-lg shadow-lg"
            >
              Upgrade Subscription
            </button>
          </div>
          <SubscriptionSelector 
            onSelected={async (tier) => {
              const success = await useSubscriptionStore.getState().upgradeTier(tier as any);
              if (success) {
                setShowUpgrade(false);
                setError(null);
                // Recalculate design
                const result = await calculateDesign(location);
                if (result) {
                  setDesign(result.design);
                  setFinancials(result.financials);
                  setBOQ(result.boq);
                  setCalculationResults(result.calculationResults ?? null);
                  onDesignCalculated(result.design, result.financials, result.boq, {
                    calculationResults: result.calculationResults ?? null,
                    calculationInputs: result.calculationInputs ?? null,
                    pricingFactors: result.pricingFactors ?? null,
                    moduleSnapshot: result.moduleSnapshot ?? null,
                    inverterSnapshot: result.inverterSnapshot ?? null,
                  });
                }
              }
            }}
            showUpgradeOnly={true}
          />
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 border border-gray-200">
          {error && (
            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-yellow-800">{error}</p>
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row justify-between items-start mb-6">
            <div className="flex items-center mb-4 sm:mb-0">
              <button
                onClick={onBack}
                className="mr-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Go back"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Energy System Technical Analysis</h2>
            </div>
            <div className="text-sm text-gray-600">
              Based on Saudi Arabia's average sunlight and dust conditions
            </div>
          </div>

          {/* System Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-12">
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
              <h3 className="text-xl font-semibold mb-4 sm:mb-6 flex items-center">
                <Settings className="h-6 w-6 text-blue-600 mr-2" />
                System Parameters
              </h3>

              {(selectedModule || selectedInverter) && (
                <div className="mb-5 bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <p className="text-sm font-semibold text-gray-800 mb-2">Selected Equipment</p>
                  <p className="text-xs text-gray-500 mb-2">
                    USD prices converted using {pricingFactors?.usdToSar ?? 3.75} SAR/USD.
                  </p>
                  <div className="space-y-1 text-sm text-gray-700">
                    {selectedModule && (
                      <div>
                        <span className="font-medium">Module:</span> {selectedModule.manufacturer} {selectedModule.model} ({selectedModule.power_stc}W) — ${selectedModule.price_usd}/module
                      </div>
                    )}
                    {selectedInverter && (
                      <div>
                        <span className="font-medium">Inverter:</span> {selectedInverter.manufacturer} {selectedInverter.model} ({selectedInverter.power_ac_nominal}kW) — ${selectedInverter.price_usd}/unit
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">System Capacity</p>
                  <p className="text-lg font-semibold">{design.systemSize.toFixed(1)} kW</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Number of Panels</p>
                  <p className="text-lg font-semibold">{design.panelCount} panels</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Yearly Production</p>
                  <p className="text-lg font-semibold">{Math.round(design.yearlyProduction).toLocaleString()} kWh</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Energy Offset</p>
                  <p className="text-lg font-semibold">{displayedOffsetPct}%</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
              <h3 className="text-xl font-semibold mb-4 sm:mb-6 flex items-center">
                <MapPin className="h-6 w-6 text-green-600 mr-2" />
                Location Parameters
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Latitude</p>
                  <p className="text-lg font-semibold">{(location.latitude || 24.7136).toFixed(4)}°N</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Longitude</p>
                  <p className="text-lg font-semibold">{(location.longitude || 46.6753).toFixed(4)}°E</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Tilt Angle</p>
                  <p className="text-lg font-semibold">{location.tilt}°</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Azimuth</p>
                  <p className="text-lg font-semibold">{location.azimuth}°</p>
                </div>
              </div>
            </div>
          </div>

          {/* Energy Production Chart */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 mb-8 shadow-sm">
            <h3 className="text-xl font-semibold mb-4 sm:mb-6 flex items-center">
              <Zap className="h-6 w-6 text-yellow-500 mr-2" />
              Monthly Energy Production
            </h3>
            <div className="h-64 sm:h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={generateMonthlyData(design)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis label={{ value: 'kWh', angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="consumption" stroke="#8884d8" name="Energy Consumption" />
                  <Line type="monotone" dataKey="generation" stroke="#82ca9d" name="Solar Generation" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Financial Analysis */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 mb-8 shadow-sm">
            <h3 className="text-xl font-semibold mb-4 sm:mb-6 flex items-center">
              <DollarSign className="h-6 w-6 text-green-600 mr-2" />
              Financial Analysis
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div>
                <h4 className="text-lg font-medium mb-4">System Costs</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total System Cost (BOQ incl. VAT)</span>
                    <span className="font-medium">SAR {financials.totalCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="text-lg font-medium mb-4">Savings Projection</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Monthly Savings</span>
                    <span className="font-medium">SAR {(financials.savingsYear1 / 12).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">First Year Savings</span>
                    <span className="font-medium">SAR {financials.savingsYear1.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">25-Year Savings</span>
                    <span className="font-medium">SAR {financials.savings25Year.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-gray-200">
                    <span className="text-gray-800 font-medium">ROI</span>
                    <span className="font-medium">{financials.roi.toFixed(1)}%</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="h-64 sm:h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={generateMonthlyData(design)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis label={{ value: 'SAR', angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="savings" fill="#8884d8" name="Monthly Savings" />
                  <Bar dataKey="newBill" fill="#82ca9d" name="Projected Bill" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-6 bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                Note: Results can be improved with regular panel cleaning to minimize dust-related efficiency losses.
                The calculations account for Saudi Arabia's climate conditions and tariff assumptions.
              </p>
            </div>
          </div>

          {/* BOQ Section */}
          {boq && (
            <div className="mt-8">
              <button
                onClick={() => setShowBOQ(!showBOQ)}
                className="mb-4 text-blue-600 hover:text-blue-700 font-medium flex items-center gap-2"
              >
                <FileText className="h-5 w-5" />
                {showBOQ ? 'Hide' : 'Show'} Detailed Bill of Quantities
              </button>
              
              {showBOQ && <BOQDisplay boq={boq} systemSizeKw={design?.systemSize} />}
            </div>
          )}

          {/* Navigation */}
          <div className="flex flex-col sm:flex-row justify-between items-center mt-8 space-y-4 sm:space-y-0 gap-4">
            <button
              onClick={onBack}
              className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 flex items-center justify-center transition-colors w-full sm:w-auto"
            >
              <Compass className="h-5 w-5 mr-2" />
              Back to Location
            </button>
            
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              {design && design.monthlyResults && (
                <button
                  onClick={async () => {
                    try {
                      const monthlyResults = design.monthlyResults;
                      if (!monthlyResults) return;

                      const cityData = findNearestCity(
                        location.locations?.[0]?.latitude || location.latitude || 24.7136,
                        location.locations?.[0]?.longitude || location.longitude || 46.6753
                      );
                      const projectNameBase = `Solar_Project_${new Date().toISOString().split('T')[0]}`;
                      const persistPdf = Boolean(projectId && user);
                      const blob = await generatePDFReport({
                        projectName: projectNameBase,
                        locationName: cityData.name,
                        latitude: location.locations?.[0]?.latitude || location.latitude || 24.7136,
                        longitude: location.locations?.[0]?.longitude || location.longitude || 46.6753,
                        altitude: cityData.altitude,
                        tilt: location.locations?.[0]?.tilt || location.tilt || 10,
                        azimuth: location.locations?.[0]?.azimuth || location.azimuth || 180,
                        mountingType: location.locations?.[0]?.mounting_type || location.mountingType || 'roof-fixed',
                        surfaceType: location.locations?.[0]?.surface_type || location.surfaceType || 'asphalt',
                        monthlyConsumption: getEffectiveConsumption(billData).effectiveMonthlyConsumption,
                        systemPower: design.systemSize,
                        annualProduction: design.yearlyProduction,
                        specificProduction: design.yearlyProduction / design.systemSize,
                        performanceRatio: monthlyResults.reduce((sum, m) => sum + m.performanceRatio, 0) / 12,
                        monthlyResults: monthlyResults.map(m => ({
                          month: m.month,
                          globHor: m.ghi,
                          diffHor: m.ghi * 0.3,
                          tempAmb: m.temp,
                          globInc: m.poa,
                          globEff: m.poa,
                          eArray: m.arrayOutput,
                          eGrid: m.gridOutput,
                          pr: m.performanceRatio
                        })),
                        systemConfig: {
                          modulesPerString: Math.ceil(design.panelCount / 2),
                          stringsParallel: 2,
                          nInverters:
                            (boq?.items.find((i) => i.category === 'Inverters')?.quantity as number | undefined) ?? 1,
                          modulePower: selectedModule?.power_stc || 550,
                          inverterPower: design.selectedInverterAcKw ?? (selectedInverter?.power_ac_nominal ?? (design.systemSize / 1.3)),
                          totalModules: design.panelCount,
                          dcAcRatio: design.dcAcRatioFinal,
                        },
                        boq: boq || undefined
                      }, persistPdf ? { disposition: 'both' } : undefined);
                      if (blob instanceof Blob && projectId && user) {
                        await uploadPdfAndRecord({
                          projectId,
                          designId: pvDesignId,
                          docType: 'solar_report_pdf',
                          blob,
                          storageFileBase: projectNameBase,
                          filesAppendType: 'solar_report_pdf',
                        });
                      }
                    } catch (error) {
                      console.error('PDF generation error:', error);
                      alert('Failed to generate PDF report. Please try again.');
                    }
                  }}
                  className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 flex items-center justify-center transition-colors w-full sm:w-auto"
                >
                  <Download className="h-5 w-5 mr-2" />
                  Download Report
                </button>
              )}
              {design && design.monthlyResults && (
                <button
                  onClick={async () => {
                    try {
                      const lat = location.locations?.[0]?.latitude ?? location.latitude ?? 24.7136;
                      const lon = location.locations?.[0]?.longitude ?? location.longitude ?? 46.6753;
                      const cityData = findNearestCity(lat, lon);
                      const effectiveConsumption = getEffectiveConsumption(billData);
                      const monthlyConsumption = effectiveConsumption.effectiveMonthlyConsumption;
                      const yearlyConsumption = effectiveConsumption.effectiveYearlyConsumption;
                      const fin = financials ?? {
                        totalCost: 0,
                        paybackPeriod: 0,
                        roi: 0,
                        savingsYear1: 0,
                        savings25Year: 0,
                        monthlyPayment: 0,
                      };
                      const projectNameBase = `Solar_Project_${new Date().toISOString().split('T')[0]}`;
                      const persistPdf = Boolean(projectId && user);
                      const sourcesData: CalculationSourcesData = {
                        projectName: projectNameBase,
                        locationName: cityData.name,
                        latitude: lat,
                        longitude: lon,
                        altitude: cityData.altitude,
                        tilt: location.locations?.[0]?.tilt ?? location.tilt ?? 10,
                        azimuth: location.locations?.[0]?.azimuth ?? location.azimuth ?? 180,
                        mountingType: location.locations?.[0]?.mounting_type ?? location.mountingType ?? 'roof-fixed',
                        surfaceType: location.locations?.[0]?.surface_type ?? location.surfaceType ?? 'asphalt',
                        userType: userType ?? 'residential',
                        monthlyConsumption,
                        yearlyConsumption,
                        baseYearlyConsumption: effectiveConsumption.baseYearlyConsumption,
                        morningConsumptionPct: effectiveConsumption.morningConsumptionPct,
                        effectiveYearlyConsumption: effectiveConsumption.effectiveYearlyConsumption,
                        customTariffSarPerKwh: billData?.customTariffSarPerKwh,
                        gridPhase: location.electrical?.gridPhase,
                        acCableDistanceM: location.electrical?.acCableDistanceM,
                        dcCableDistanceM: location.electrical?.dcCableDistanceM,
                        design: {
                          systemSize: design.systemSize,
                          panelCount: design.panelCount,
                          yearlyProduction: design.yearlyProduction,
                          offset:
                            yearlyConsumption > 0
                              ? Math.min(100, Math.round((design.yearlyProduction / yearlyConsumption) * 100))
                              : 0,
                          initialSystemSize: design.initialSystemSize,
                          inverterAcCapKw: design.inverterAcCapKw,
                          selectedInverterAcKw: design.selectedInverterAcKw,
                          selectedInverterCount: design.selectedInverterCount,
                          dcAcRatioFinal: design.dcAcRatioFinal,
                          monthlyResults: design.monthlyResults,
                        },
                        calculationResults,
                        financials: {
                          totalCost: fin.totalCost,
                          paybackPeriod: fin.paybackPeriod,
                          roi: fin.roi,
                          savingsYear1: fin.savingsYear1,
                          savings25Year: fin.savings25Year,
                          monthlyPayment: fin.monthlyPayment,
                        },
                        boq,
                        selectedModule: selectedModule ? { manufacturer: selectedModule.manufacturer, model: selectedModule.model, power_stc: selectedModule.power_stc } : null,
                        selectedInverter: selectedInverter ? { manufacturer: selectedInverter.manufacturer, model: selectedInverter.model, power_ac_nominal: selectedInverter.power_ac_nominal } : null,
                      };
                      const blob = await generateCalculationSourcesPDF(
                        sourcesData,
                        persistPdf ? { disposition: 'both' } : undefined
                      );
                      if (blob instanceof Blob && projectId && user) {
                        await uploadPdfAndRecord({
                          projectId,
                          designId: pvDesignId,
                          docType: 'calculation_sources_pdf',
                          blob,
                          storageFileBase: `${projectNameBase}_calc_sources`,
                          filesAppendType: 'calculation_sources_pdf',
                        });
                      }
                    } catch (error) {
                      console.error('Calculation sources PDF error:', error);
                      alert('Failed to generate calculation sources document. Please try again.');
                    }
                  }}
                  className="bg-slate-600 text-white px-6 py-3 rounded-lg hover:bg-slate-700 flex items-center justify-center transition-colors w-full sm:w-auto"
                >
                  <FileText className="h-5 w-5 mr-2" />
                  Calculation Sources
                </button>
              )}
              <button
                onClick={onNext}
                className="bg-gradient-to-r from-blue-600 to-green-600 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-green-700 flex items-center justify-center transition-colors w-full sm:w-auto"
              >
                Next: System Details
                <Sun className="h-5 w-5 ml-2" />
              </button>
            </div>
            
            {/* Accuracy Disclaimer */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-900">
                  <p className="font-semibold mb-1">Preliminary Estimate</p>
                  <p className="text-blue-800">
                    This calculation uses industry-standard PVSyst-inspired methodology with an expected accuracy of ±5-7% for standard configurations. 
                    Results are suitable for initial feasibility assessment and project planning. For systems larger than 100 kWp or complex installations, 
                    professional validation with detailed site analysis is recommended.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="mt-12">
        <Footer />
      </div>
    </div>
  );
}
